import { DEVELOPMENT_TIERS, TIER_INFO, TIERS, formatUSD, type Tier } from '../lib/quoteEstimator';

/**
 * Presentation data for the development pricing list and the per-tier pages.
 * Prices come ONLY from DEVELOPMENT_TIERS / TIER_INFO in quoteEstimator.ts —
 * this file supplies plain-language names, reasons, and examples around them.
 */
export interface IndustryRange {
  /** Directional one-time range from published industry research. */
  range: string;
  /** Closest project category used by the source. */
  category: string;
}

/**
 * Directional comparison only — not a FairCorp price, quote, or promise.
 * Ranges come from the GoodFirms 2026 survey of 300+ web development firms
 * (basic site, mid-sized/CMS, custom app, and enterprise bands), mapped to the
 * closest FairCorp tier. See INDUSTRY_RANGE_NOTE in Pricing.tsx.
 */
export const INDUSTRY_RANGES: Record<Tier, IndustryRange> = {
  T1: { range: '$1,000–$3,000', category: 'Basic website / MVP' },
  T2: { range: '$5,000–$20,000', category: 'Mid-sized business site' },
  T3A: { range: '$5,000–$20,000', category: 'Custom-designed site' },
  T3B: { range: '$10,000–$40,000', category: 'Custom site with integrations' },
  T4A: { range: '$20,000–$50,000', category: 'Custom web application' },
  T4B: { range: '$20,000–$50,000', category: 'Custom web app / portal' },
  T5: { range: '$20,000–$200,000+', category: 'Enterprise website / platform' },
};

export interface TierDeepDive {
  heading: string;
  intro: string;
  paragraphs: string[];
  signals: string[];
}

export interface TierSummary {
  tier: Tier;
  /** Short label used in the comparison list, e.g. "Tier 1". */
  label: string;
  /** Plain-language name, e.g. "Single-page website". */
  name: string;
  description: string;
  priceDisplay: string;
  priceNote: string;
  /** Standard reasons a project lands in this tier. */
  reasons: string[];
  /** Features included in the tier (from the approved tier reference). */
  includes: string[];
  bestFor: string;
  notFor: string;
  hostingRange: string;
  href: string;
  /** Extra explanation for tiers that need more than a one-line summary. */
  deepDive?: TierDeepDive;
}

const PLAIN_NAMES: Record<Tier, string> = {
  T1: 'Single-page website',
  T2: 'Multi-page website',
  T3A: 'Advanced design and motion',
  T3B: 'Third-party integrations',
  T4A: 'Custom backend application',
  T4B: 'Database-backed application',
  T5: 'High-reliability platform',
};

const REASONS: Record<Tier, string[]> = {
  T1: [
    'You need one polished page',
    'A simple responsive layout is enough',
    'No accounts, database, or custom backend',
    'You want the lowest-cost custom build',
  ],
  T2: [
    'You need several pages of information',
    'You want custom navigation and branding',
    'You need service, product, or team pages',
    'No custom application logic',
  ],
  T3A: [
    'Design and motion are central to the brand',
    'You need custom animation or interactive visuals',
    'The site is still primarily a presentation',
    'No custom backend or stored data',
  ],
  T3B: [
    'You already use an outside service for orders, booking, or checkout',
    'You need that service connected into a custom experience',
    'The provider keeps responsibility for its own service',
    'You may need a small integration service hosted by FairCorp',
  ],
  T4A: [
    'You need custom business logic or a server-side API',
    'The work goes beyond a content or integration site',
    'You do not need a large application database',
    'The final price is confirmed by an engineering quote',
  ],
  T4B: [
    'You need to store records, accounts, or customer data',
    'You need authentication and database-backed operations',
    'The app must read and update persistent data',
    'The final price is confirmed by an engineering quote',
  ],
  T5: [
    'Reliability, redundancy, or recovery are critical',
    'You handle sensitive or regulated data',
    'The platform spans multiple systems or very high traffic',
    'You need a custom engineering and security assessment',
  ],
};

const NOT_FOR: Record<Tier, string> = {
  T1: 'More than one or two pages, accounts, or custom app features.',
  T2: 'Custom application logic, customer accounts, or stored data.',
  T3A: 'Custom backend processing or a persistent database.',
  T3B: 'Original processing of your business data or long-term storage.',
  T4A: 'A substantial application database or accounts you need us to store.',
  T4B: 'A straightforward brochure or informational site.',
  T5: 'A simple site that does not carry unusual reliability or data duties.',
};

const DEEP_DIVES: Partial<Record<Tier, TierDeepDive>> = {
  T5: {
    heading: 'What a T5 site usually looks like',
    intro:
      'T5 is the top development tier. It is used when the platform itself carries business-critical risk — not simply because it has more pages or features.',
    paragraphs: [
      'A T5 project usually combines several of these: sensitive or regulated records, an expectation of continuous availability, heavy or unpredictable traffic, infrastructure spread across multiple providers, or security obligations that affect the architecture. Some T5 sites are database-backed applications. Others have no application database but still need failover, hardened operations, or specialist security controls.',
      'The work begins with architecture and risk review. We define failure modes, access boundaries, backup and recovery expectations, monitoring, and the operating responsibilities of each provider before committing to a build. Any availability, recovery-time, support-hours, or regulatory commitment is agreed in the contract; this page does not invent one.',
      'Because the scope is open-ended, the $18,000 figure is a starting point, not a formula result. Final pricing depends on the systems involved, the security work, the recovery design, and the level of operational support. F5 exposure may require specialist controls, insurance, external expertise, or a decision to decline the commission.',
    ],
    signals: [
      'Sensitive, regulated, or high-value data with strict access and audit needs',
      'Availability or recovery expectations beyond a standard hosting plan',
      'High traffic or load spread across multiple services and providers',
      'A security review that changes the architecture, testing, or operating model',
    ],
  },
};

export const TIER_SUMMARIES: TierSummary[] = TIERS.map((tier) => {
  const info = TIER_INFO.find((t) => t.tier === tier)!;
  const range = DEVELOPMENT_TIERS[tier];
  const priceDisplay = range.max === null ? `From ${formatUSD(range.min)}` : `${formatUSD(range.min)} – ${formatUSD(range.max)}`;

  return {
    tier,
    label: `Tier ${tier.slice(1)}`,
    name: PLAIN_NAMES[tier],
    description: info.description,
    priceDisplay,
    priceNote: range.max === null ? 'Starting price — final engineering quote' : 'Approved range — final quote after review',
    reasons: REASONS[tier],
    includes: info.features,
    bestFor: info.bestFor,
    notFor: NOT_FOR[tier],
    hostingRange: info.hostingRange,
    href: `/tiers/${tier}`,
    deepDive: DEEP_DIVES[tier],
  };
});

export const tierSummary = (tier: Tier): TierSummary => TIER_SUMMARIES.find((t) => t.tier === tier)!;

/** Tier codes used by routes and links, e.g. /tiers/t3a → T3A. */
export function normalizeTier(value: string | undefined): Tier | null {
  const candidate = (value ?? '').toUpperCase();
  return (TIERS as string[]).includes(candidate) ? (candidate as Tier) : null;
}
